import { Fragment, useEffect, useRef, type ElementType, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export type RevealVariant = "up" | "fade" | "scale" | "blur" | "words" | "stagger";

const reduced = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export type RevealOptions = {
  as?: ElementType;
  variant?: RevealVariant;
  /** Delay in MILLISECONDS before the reveal starts. */
  delay?: number;
  /** Stagger between child/word reveals in SECONDS. */
  stagger?: number;
  className?: string;
  children: ReactNode;
  /** Threshold 0-1 for when the reveal fires (IntersectionObserver). */
  amount?: number;
};

/**
 * Scroll-driven reveal using IntersectionObserver (cheap, SSR-safe).
 * GSAP/Lenis drive the smooth scroll; this handles the in-view animation
 * so it works without GSAP timeline boilerplate on every element.
 *
 * `delay` is in MILLISECONDS (matches how every page calls this). It is
 * converted to seconds where CSS/gsap require seconds.
 *
 * Variants:
 * - up / fade / scale / blur : element-level entrance
 * - words : splits children text into word spans that rise one by one
 * - stagger : fades + lifts direct children in sequence
 */
export function Reveal({
  as,
  variant = "up",
  delay = 0,
  stagger = 0.06,
  className,
  children,
  amount = 0.15,
}: RevealOptions) {
  const Tag = (as ?? "div") as ElementType;
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (reduced()) {
      el.style.opacity = "1";
      el.style.transform = "";
      el.style.filter = "";
      return;
    }

    // Initial hidden state (only on the client, after mount — SSR stays visible).
    el.classList.add("reveal-init");
    if (variant === "up") el.classList.add("reveal-up");
    if (variant === "scale") el.classList.add("reveal-scale");
    if (variant === "blur") el.classList.add("reveal-blur");

    const targets: HTMLElement[] =
      variant === "stagger"
        ? (Array.from(el.children) as HTMLElement[])
        : variant === "words"
          ? Array.from(el.querySelectorAll<HTMLElement>("[data-word]"))
          : [el];

    targets.forEach((t) => {
      t.style.opacity = "0";
      t.style.transform = variant === "words" ? "translateY(110%)" : "translateY(22px)";
      if (variant === "blur") t.style.filter = "blur(12px)";
      t.style.transition = `opacity .6s ease, transform .6s cubic-bezier(.2,.7,.2,1), filter .6s ease`;
    });

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            // Always reveal the wrapper (so the whole element is visible),
            // not just the inner target spans — critical for the "words" variant.
            el.style.opacity = "1";
            el.style.transform = "";
            el.style.filter = "";
            el.classList.remove("reveal-init", "reveal-up", "reveal-scale", "reveal-blur");

            targets.forEach((t, i) => {
              t.style.transitionDelay = `${(delay + i * stagger * 1000) / 1000}s`;
              t.style.opacity = "1";
              t.style.transform = "none";
              t.style.filter = "none";
            });
            io.disconnect();
          }
        }
      },
      { threshold: amount, rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [variant, delay, stagger, amount]);

  if (variant === "words" && typeof children === "string") {
    const words = children.split(" ");
    return (
      <Tag ref={ref as never} className={cn(className)}>
        {words.map((w, i) => (
          <Fragment key={i}>
            <span
              style={{
                display: "inline-block",
                overflow: "hidden",
                verticalAlign: "top",
                paddingBottom: "0.08em",
              }}
            >
              <span data-word style={{ display: "inline-block" }}>
                {w}
              </span>
            </span>
            {i < words.length - 1 ? " " : null}
          </Fragment>
        ))}
      </Tag>
    );
  }

  return (
    <Tag ref={ref as never} className={cn(className)}>
      {children}
    </Tag>
  );
}
